import { useEffect, useRef, useCallback } from 'react';
import { PerformanceMonitor } from '../../api/weather/utils/performanceMonitor';

interface UsePerformanceMonitorOptions {
  componentName: string;
  trackProps?: boolean;
  logThreshold?: number;
}

export const usePerformanceMonitor = (
  options: UsePerformanceMonitorOptions,
  dependencies?: React.DependencyList
) => {
  const { componentName, trackProps = false, logThreshold = 5 } = options;
  const renderCountRef = useRef(0);
  const propsRef = useRef<Record<string, unknown> | null>(null);

  // Increment render count before effect runs
  renderCountRef.current += 1;

  useEffect(() => {
    const props = trackProps && dependencies ? 
      dependencies.reduce((acc: Record<string, unknown>, dep, index) => {
        acc[`dep_${index}`] = dep;
        return acc;
      }, {}) : undefined;

    propsRef.current = props || null;

    PerformanceMonitor.trackRender(componentName, renderCountRef.current, props);

    // Log warning for excessive renders
    if (renderCountRef.current > logThreshold) {
      console.warn(
        `Component ${componentName} has rendered ${renderCountRef.current} times`,
        props
      );
    }
  });

  const resetRenderCount = useCallback(() => {
    renderCountRef.current = 0;
  }, []);

  return {
    renderCount: renderCountRef.current,
    resetRenderCount,
  };
};

export const useAPIPerformanceMonitor = () => {
  const measureAPICall = <T>(
    name: string,
    url: string,
    method: string,
    apiCall: () => Promise<T>
  ): Promise<T> => {
    return PerformanceMonitor.measureAPICall(name, url, method, apiCall);
  };

  return { measureAPICall };
};